import { ChevronRight, Link2 } from 'lucide-react';
import { Card } from '../../../shared/components/ui/Card';
import { Badge } from '../../../shared/components/ui/Badge';

interface User {
    id: string | number;
    name: string;
    email: string;
    ltv: number;
    tokens: number;
    orders: number;
    lastActive: string;
    status: string;
    intent: string;
    tags: string[];
    source?: string;
    gender?: string;
    birthDate?: string;
    lastPurchase?: string;
}

interface UserTableProps {
    users: User[];
    onSelectUser: (user: User) => void;
    showTutorial?: boolean;
}

const UserTable = ({ users, onSelectUser, showTutorial }: UserTableProps) => {
    return (
        <Card
            className="overflow-hidden border border-slate-100 shadow-sm"
            showTutorial={showTutorial}
            tutorialAlign="top"
            tutorial="Base de datos completa de tu audiencia. Aquí puedes ver el comportamiento de compra, puntos acumulados y actividad de cada cliente en tiempo real."
        >
            <div className="overflow-x-auto">
                {/* H18: min-w fuerza el scroll horizontal cuando el viewport es < 1000px */}
                <table className="w-full min-w-[1000px] text-left border-collapse">
                    <thead>
                        <tr className="bg-slate-50 border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wider">
                            <th className="p-4 font-bold">Usuario</th>
                            <th className="p-4 font-bold">Estado</th>
                            <th className="p-4 font-bold">LTV</th>
                            <th className="p-4 font-bold">Puntos</th>
                            <th className="p-4 font-bold">Pedidos</th>
                            <th className="p-4 font-bold">Fecha de Nacimiento</th>
                            <th className="p-4 font-bold">Género</th>
                            <th className="p-4 font-bold">Fuente</th>
                            <th className="p-4 font-bold">Última Compra</th>
                            <th className="p-4 font-bold">Última Actividad</th>
                            <th className="p-4 font-bold"></th>
                        </tr>
                    </thead>
                    <tbody className="text-sm text-slate-900 divide-y divide-slate-100">
                        {users.map((user) => (
                            <tr
                                key={user.id}
                                onClick={() => onSelectUser(user)}
                                className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                            >
                                <td className="p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 border border-slate-200">
                                            {user.name.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="text-slate-900 font-bold text-sm">{user.name}</div>
                                            <div className="text-xs text-slate-500">{user.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="p-4">
                                    <Badge color={
                                        user.status === 'Activo' ? 'green' :
                                            user.status === 'Inactivo' ? 'red' :
                                                user.status === 'En riesgo' ? 'yellow' : 'violet'
                                    } className="shadow-sm">
                                        {user.status}
                                    </Badge>
                                </td>
                                <td className="p-4 font-mono font-medium text-slate-700">${user.ltv}</td>
                                <td className="p-4 font-mono font-medium text-slate-700">{user.tokens}</td>
                                <td className="p-4 font-mono font-medium text-slate-700">{user.orders}</td>
                                <td className="p-4 text-slate-500">{user.birthDate || '-'}</td>
                                <td className="p-4 text-slate-500 capitalize">{user.gender || '-'}</td>
                                <td className="p-4">
                                    {user.source === 'deenex-multi' ? (
                                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-violet-50 text-violet-700 border border-violet-200">
                                            <Link2 size={10} />
                                            Multi
                                        </span>
                                    ) : (
                                        <span className="text-xs text-slate-400">Manual</span>
                                    )}
                                </td>
                                <td className="p-4 text-slate-500 text-xs">{user.lastPurchase ? new Date(user.lastPurchase).toLocaleDateString() : '-'}</td>
                                <td className="p-4 text-slate-500 text-xs">{user.lastActive}</td>
                                <td className="p-4 text-right">
                                    <button
                                        type="button"
                                        aria-label={`Ver detalle de ${user.name}`}
                                        className="p-1.5 hover:bg-slate-200 rounded-lg text-slate-400 hover:text-slate-900 transition-colors"
                                    >
                                        <ChevronRight size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {
                users.length === 0 && (
                    <div className="p-8 text-center text-slate-500 text-sm">No se encontraron usuarios con ese criterio de búsqueda.</div>
                )
            }
        </Card >
    );
};

export default UserTable;
